/**
 * Error Boundary Component for Interview Management
 * Provides graceful error handling and recovery options
 */

import React from 'react';
import Button from '../ui/Button';
import Icon from '../AppIcon';
import ErrorHandler from '../../utils/errorHandler';

class InterviewErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    ErrorHandler.logError('InterviewErrorBoundary', error, { errorInfo });
    
    this.setState({
      error,
      errorInfo,
      retryCount: this.state.retryCount + 1
    });
  }

  handleRetry = () => {
    // Reset error state to retry rendering
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
    
    // If there's a retry callback, call it
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg border border-border p-6 text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Icon name="AlertTriangle" size={24} className="text-red-600" />
            </div>
            
            <h1 className="text-xl font-semibold text-text-primary mb-2">
              Something went wrong
            </h1>
            
            <p className="text-text-secondary mb-6">
              We encountered an error while loading the interview management page. 
              This might be a temporary issue.
            </p>
            
            <div className="space-y-3">
              {this.state.retryCount < 3 && (
                <Button
                  variant="primary"
                  onClick={this.handleRetry}
                  className="w-full"
                >
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Try Again
                </Button>
              )}
              
              <Button
                variant="outline"
                onClick={this.handleReload}
                className="w-full"
              >
                <Icon name="RotateCcw" size={16} className="mr-2" />
                Reload Page
              </Button>
              
              {this.props.fallbackUrl && (
                <Button
                  variant="ghost"
                  onClick={() => window.location.href = this.props.fallbackUrl}
                  className="w-full"
                >
                  <Icon name="Home" size={16} className="mr-2" />
                  Go to Dashboard
                </Button>
              )}
            </div>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm font-medium text-text-secondary">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping components with error boundary
export const withErrorBoundary = (Component, fallbackUrl = '/employer-dashboard') => {
  return function WrappedComponent(props) {
    return (
      <InterviewErrorBoundary fallbackUrl={fallbackUrl}>
        <Component {...props} />
      </InterviewErrorBoundary>
    );
  };
};

export default InterviewErrorBoundary;